import LinkText from 'ext-ui/components/LinkText/LinkText'
import React, { MouseEvent, ReactElement } from 'react'

import { isPlainLeftClick } from './navigationClick'
import { navLink } from './statNavLink.style'

export type StatNavLinkProps = {
  /** Destination, built by the mounting page — features do not know URLs. */
  href: string
  /**
   * SPA navigation. When omitted the anchor falls back to a full page load,
   * which is correct but slow — pages should always pass this.
   */
  onNavigate?: (href: string) => void
  children: React.ReactNode
  className?: string
}

/**
 * A real `<a href>` that navigates without a page reload.
 *
 * Rendering an anchor rather than a button is not cosmetic: it is what gives the
 * link its role, its "open in new tab" affordance, its status-bar preview, and
 * keyboard behavior. So the href is always present and honest, and `onNavigate`
 * only intercepts the plain left-click that would otherwise reload the app.
 *
 * Modified clicks (⌘/Ctrl/Shift/Alt), and non-primary buttons, fall through to
 * the browser untouched — intercepting those is the classic SPA bug that breaks
 * open-in-new-tab.
 *
 * Features cannot import the router, so the page supplies both halves.
 */
export default function StatNavLink(props: StatNavLinkProps): ReactElement {
  const { href, onNavigate, children, className } = props

  const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (!onNavigate) return
    if (!isPlainLeftClick(event)) return

    event.preventDefault()
    onNavigate(href)
  }

  return (
    <LinkText
      css={navLink}
      href={href}
      onClick={handleClick}
      className={className}
    >
      {children}
    </LinkText>
  )
}
